import type { PaymentFrequency, PaymentMethod, Renewal, RenewalNotification, RenewalRisk, RenewalType, RenewalWorkflowStatus, RenewalWorkflowStep, SmsDeliveryStatus } from '@/types';
import type { RenewalDetailResponseDto, RenewalListItemDto } from '@/api/config';
import { parseRenewalLostMeta } from './renewalLostMeta';

const workflowStatusMap: Record<string, RenewalWorkflowStatus> = {
  PENDING: 'pending',
  AWAITING_CLIENT_RESPONSE: 'awaiting-client-response',
  CONFIRMED_NO_CHANGE: 'confirmed-no-change',
  REQUEST_CALLBACK: 'request-callback',
  PENDING_AMENDMENTS: 'pending-amendments',
  AWAITING_UPDATED_PRICING: 'awaiting-updated-pricing',
  UPDATED_TERMS_RECEIVED: 'updated-terms-received',
  PRICING_FINALISED: 'pricing-finalised',
  SENT_TO_CLIENT: 'sent-to-client',
  AWAITING_PAYMENT: 'awaiting-payment',
  RENEWED_SUCCESSFULLY: 'renewed-successfully',
  LAPSED: 'lapsed',
  LOST: 'lost',
  INSURER_REVIEW: 'insurer-review',
};

const renewalTypeMap: Record<string, RenewalType> = {
  AUTOMATIC: 'automatic',
  MANUAL: 'manual',
};

type ApiRenewalWorkflowStatus =
  | 'PENDING'
  | 'AWAITING_CLIENT_RESPONSE'
  | 'CONFIRMED_NO_CHANGE'
  | 'REQUEST_CALLBACK'
  | 'PENDING_AMENDMENTS'
  | 'AWAITING_UPDATED_PRICING'
  | 'UPDATED_TERMS_RECEIVED'
  | 'PRICING_FINALISED'
  | 'SENT_TO_CLIENT'
  | 'AWAITING_PAYMENT'
  | 'RENEWED_SUCCESSFULLY'
  | 'LAPSED'
  | 'LOST'
  | 'INSURER_REVIEW';
type ApiRenewalType = 'AUTOMATIC' | 'MANUAL';

const workflowStatusToApiMap = Object.fromEntries(
  Object.entries(workflowStatusMap).map(([api, frontend]) => [frontend, api]),
) as Record<RenewalWorkflowStatus, ApiRenewalWorkflowStatus>;

const renewalTypeToApiMap = Object.fromEntries(
  Object.entries(renewalTypeMap).map(([api, frontend]) => [frontend, api]),
) as Record<RenewalType, ApiRenewalType>;

export function mapRenewalWorkflowStatusToApi(
  status: RenewalWorkflowStatus,
): ApiRenewalWorkflowStatus {
  return workflowStatusToApiMap[status];
}

export function mapRenewalTypeToApi(type: RenewalType): ApiRenewalType {
  return renewalTypeToApiMap[type];
}

const paymentFrequencyMap: Record<string, PaymentFrequency> = {
  ANNUAL: 'annual',
  MONTHLY: 'monthly',
  FORTNIGHTLY: 'fortnightly',
};

const paymentMethodMap: Record<string, PaymentMethod> = {
  DIRECT_DEBIT: 'direct-debit',
  CREDIT_CARD: 'credit-card',
  BANK_TRANSFER: 'bank-transfer',
  PREMIUM_FUNDING: 'premium-funding',
  OTHER: 'other',
};

const riskTypeMap: Record<string, RenewalRisk['type']> = {
  'PERSONAL LINES - HOME RISK': 'house',
  'PERSONAL LINES - CONTENTS RISK': 'contents',
  'PERSONAL LINES - VEHICLE RISK': 'vehicle',
};

const fallbackDate = new Date().toISOString();

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
};

const mapRisk = (risk: any, index: number, currentRiskByKey?: Map<string, RenewalRisk>): RenewalRisk => {
  const terms = risk?.risk_details?.terms ?? {};
  const premiums = terms?.risk_premiums ?? {};
  const excesses = Array.isArray(terms?.excesses) ? terms.excesses : [];

  const currentPremium = toNumber(premiums?.premium_due_gross, 0);
  const riskExcess = toNumber(excesses?.[0]?.total_excess, 0);
  const sumInsured = toNumber(risk?.risk_details?.coverage?.total_sum_insured, 0);
  const riskKey = String(risk?.risk_key ?? `renewal-risk-${index}`);
  const matchedCurrent = currentRiskByKey?.get(riskKey);

  return {
    id: riskKey,
    type: riskTypeMap[String(risk?.risk_type)] ?? 'house',
    description: String(risk?.risk_description ?? `Risk ${index + 1}`),
    currentSumInsured: matchedCurrent?.currentSumInsured ?? sumInsured,
    newSumInsured: sumInsured,
    currentExcess: matchedCurrent?.currentExcess ?? riskExcess,
    newExcess: riskExcess,
    currentPremium: matchedCurrent?.currentPremium ?? currentPremium,
    newPremium: currentPremium,
    changes: [],
  };
};

const mapWorkflowStep = (step: {
  status: string;
  label: string;
  description: string;
  completedAt?: string | null;
  triggeredBy?: string | null;
}): RenewalWorkflowStep => ({
  status: workflowStatusMap[step.status] ?? 'pending',
  label: step.label,
  description: step.description,
  completedAt: step.completedAt ?? null,
  triggeredBy: step.triggeredBy ?? null,
});

const mapNotification = (notification: {
  id: string;
  type: string;
  sentAt: string;
  status: string;
  errorMsg?: string | null;
  deliveryStatus?: string | null;
  deliveryStatusCode?: string | null;
  deliveryUpdatedAt?: string | null;
  providerMessageId?: string | null;
}): RenewalNotification => ({
  id: notification.id,
  type: notification.type,
  sentAt: notification.sentAt,
  status: notification.status,
  errorMsg: notification.errorMsg ?? null,
  deliveryStatus: (notification.deliveryStatus as SmsDeliveryStatus | null) ?? null,
  deliveryStatusCode: notification.deliveryStatusCode ?? null,
  deliveryUpdatedAt: notification.deliveryUpdatedAt ?? null,
  providerMessageId: notification.providerMessageId ?? null,
});

export function transformRenewalToFrontend(
  apiRenewal: RenewalListItemDto | RenewalDetailResponseDto,
): Renewal {

  //current risks
  const currentRisksRaw = Array.isArray(apiRenewal.currentRisks) ? apiRenewal.currentRisks : [];
  const currentRisks = currentRisksRaw.map((risk, index) => mapRisk(risk, index));
  const currentRiskByKey = new Map(currentRisks.map((risk) => [risk.id, risk]));

  const newRisksRaw = Array.isArray(apiRenewal.newRisks) ? apiRenewal.newRisks : [];
  const newRisks = newRisksRaw.map((risk, index) => mapRisk(risk, index, currentRiskByKey));

  const currentPremium = apiRenewal.currentPremium ?? 0;
  const newPremium = apiRenewal.newPremium ?? currentPremium;
  const premiumDifference = apiRenewal.premiumDifference ?? newPremium - currentPremium;
  const premiumDifferencePercent =
    currentPremium > 0 ? (premiumDifference / currentPremium) * 100 : 0;
  const workflowStatus = workflowStatusMap[apiRenewal.workflowStatus] ?? 'pending';
  const renewalDate = apiRenewal.renewalDate ?? fallbackDate;

  const detailRenewal = apiRenewal as RenewalDetailResponseDto;
  const workflowSteps = Array.isArray(detailRenewal.workflowSteps)
    ? detailRenewal.workflowSteps.map(mapWorkflowStep)
    : [];
  const notifications = Array.isArray(detailRenewal.notifications)
    ? detailRenewal.notifications.map(mapNotification)
    : [];

  return {
    id: apiRenewal.id,
    policyId: apiRenewal.policyId,
    clientId: apiRenewal.clientId,
    clientName: apiRenewal.clientName,
    renewalNumber: apiRenewal.renewalNumber,
    renewalType: renewalTypeMap[apiRenewal.renewalType] ?? 'automatic',
    paymentFrequency: paymentFrequencyMap[apiRenewal.paymentFrequency ?? ''] ?? 'annual',
    paymentMethod: paymentMethodMap[apiRenewal.paymentMethod ?? ''] ?? null,
    currentExpiryDate: renewalDate,
    renewalDate,
    workflowStartDate: apiRenewal.createdAt ?? fallbackDate,
    currentPolicyNumber: apiRenewal.policyReference ?? apiRenewal.policyId,
    currentPremium,
    currentRisks: currentRisks,
    newPremium,
    premiumDifference,
    premiumDifferencePercent,
    newRisks,
    status: workflowStatus,
    policyReference: apiRenewal.policyReference,
    statusHistory: [
      {
        id: `status-${apiRenewal.id}`,
        status: workflowStatus,
        timestamp: apiRenewal.updatedAt ?? fallbackDate,
        performedBy: 'system',
      },
    ],
    insurer: 'Ando',
    assignedAdviser: apiRenewal.assignedAdviserId ?? '',
    assignedAdviserName: apiRenewal.assignedAdviserName ?? 'Unassigned',
    createdAt: apiRenewal.createdAt ?? fallbackDate,
    updatedAt: apiRenewal.updatedAt ?? fallbackDate,
    renewalQuoteId: apiRenewal.renewalQuoteId ?? null,
    renewalQuoteStatus: apiRenewal.renewalQuoteStatus ?? null,
    renewedPolicyId: apiRenewal.renewedPolicyId ?? null,
    renewedPolicyReference: apiRenewal.renewedPolicyReference ?? null,
    renewedPolicyStartDate: apiRenewal.renewedPolicyStartDate ?? null,
    autoRenewalDisabledAt: detailRenewal.autoRenewalDisabledAt ?? null,
    renewalDecisionReason: detailRenewal.renewalDecisionReason ?? null,
    eOfferCompletedAt: detailRenewal.eOfferCompletedAt ?? null,
    meta: parseRenewalLostMeta(detailRenewal.meta),
    lapsedAt: detailRenewal.lapsedAt ?? null,
    notifications,
    workflowSteps,
  };
}
